import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { NavigationBar } from './Navigation';
import { HRDashboard } from '../pages/Dashboard';
import { QuestionnaireEditorPage } from '../pages/QuestionnaireEditor';
import { ResultsPage } from '../pages/ResultsPage';
import { BewerbungspoolPage } from '../pages/BewerbungspoolPage';
import { SettingsPage } from '../pages/SettingsPage';
import { StatistikenPage } from '../pages/StatistikenPage';
import { StellenanzeigenPage } from '../pages/StellenanzeigenPage';
import { injectCSS } from '../../utils/cssInjection';

// Reads the active page from the URL. Module scope so it can seed useState
// below — a const arrow function inside the component would sit in the temporal
// dead zone at that point.
const getCurrentPageFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  const page = params.get('page') || 'dashboard';
  const id = params.get('id');
  return { page, id };
};

// Admin App Component (with modern responsive layout)
export function AdminApp({ user, handleLogout }) {
  // Seeded from the URL, not from a fixed 'dashboard'. Starting on the
  // dashboard meant that opening ?page=results mounted the dashboard first and
  // threw its fetches away — ~15 MB per load.
  const [currentPage, setCurrentPage] = useState(getCurrentPageFromURL);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {}
    return 'light';
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Function to update URL without causing a page reload
  const updateURL = (page, id = null) => {
    const url = new URL(window.location);
    if (page === 'dashboard') {
      url.searchParams.delete('page');
      url.searchParams.delete('id');
    } else {
      url.searchParams.set('page', page);
      if (id) {
        url.searchParams.set('id', id);
      } else {
        url.searchParams.delete('id');
      }
    }
    window.history.pushState({ page, id }, '', url.toString());
  };



  // Initialize page from URL on component mount
  useEffect(() => {
    const { page, id } = getCurrentPageFromURL();
    setCurrentPage({ page, id });
  }, []);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const { page, id } = getCurrentPageFromURL();
      setCurrentPage({ page, id });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);



  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
    if (theme === 'dark') document.body.classList.add('dark'); else document.body.classList.remove('dark');
    try { localStorage.setItem('theme', theme); } catch {}
  }, [theme]);

  // Ensure CSS is injected when AdminApp mounts
  useEffect(() => {
    injectCSS();
  }, []);

  const navigateTo = (page, id = null) => {
    setCurrentPage({ page, id });
    updateURL(page, id);
  };

  // Regular admin interface
  let content = null;
  switch (currentPage.page) {
    case 'questionnaire-editor':
      content = <QuestionnaireEditorPage navigateTo={navigateTo} />;
      break;
    case 'results':
      content = <ResultsPage navigateTo={navigateTo} detailId={currentPage.id} />;
      break;
    case 'bewerbungspool':
      content = <BewerbungspoolPage navigateTo={navigateTo} />;
      break;
    case 'settings':
      content = <SettingsPage navigateTo={navigateTo} />;
      break;
    case 'statistiken':
      content = <StatistikenPage navigateTo={navigateTo} />;
      break;
    case 'stellenanzeigen':
      content = <StellenanzeigenPage navigateTo={navigateTo} />;
      break;
    case 'dashboard':
    default:
      content = <HRDashboard navigateTo={navigateTo} />;
  }

  useEffect(() => {
    const base = 'HR-Dashboard';
    let title = base;
    
    // Check if we're on karrieresichern.de domain
    if (window.location.hostname === 'karrieresichern.de') {
      title = 'Karrieresichern';
    } else {
      // Check if we're on a landing page route
      const currentPath = window.location.pathname;
      const publicRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/?$/;
      const editRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/edit\/?$/;
      
      const publicMatch = currentPath.match(publicRoutePattern);
      const editMatch = currentPath.match(editRoutePattern);
      
      if (editMatch) {
        // Landing page editor
        const [, companyName, campaignName, landingPageName] = editMatch;
        const pageName = landingPageName || 'landing';
        title = `Landing Page Editor - ${companyName} - ${campaignName} - ${pageName}`;
      } else if (publicMatch) {
        // Public landing page
        const [, companyName, campaignName, landingPageName] = publicMatch;
        const pageName = landingPageName || 'landing';
        title = `${companyName} - ${campaignName} - ${pageName}`;
      } else {
        // Admin pages
        switch (currentPage.page) {
          case 'dashboard':
            title = 'Dashboard';
            break;
          case 'questionnaire-editor':
            title = 'Fragebogen-Editor';
            break;
          case 'results':
            title = 'Ergebnisse';
            break;
          case 'bewerbungspool':
            title = 'Bewerbungspool';
            break;
          case 'stellenanzeigen':
            title = 'Stellenanzeigen';
            break;
          case 'settings':
            title = 'Einstellungen';
            break;
          default:
            title = base;
        }
      }
    }
    
    document.title = title;
  }, [currentPage.page, currentPage.id]);

  return (
    <div className="min-h-screen relative bg-linear-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-all duration-500">
      {/* Very subtle light mode pattern */}
      <div className="absolute inset-0 dark:hidden opacity-20" style={{
        backgroundImage: `radial-gradient(circle, rgba(148, 163, 184, 0.15) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }}></div>
      
      {/* Navigation Bar */}
      <NavigationBar 
        currentPage={currentPage}
        navigateTo={navigateTo}
        user={user}
        handleLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Main Content */}
      <main className="relative">
        {content}
      </main>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '12px',
            background: '#0f172a',
            color: '#f8fafc',
            border: '1px solid #334155'
          }
        }}
      />
    </div>
  );
}
